import React from "react";
import {
  DecisionPageLayout,
  DecisionSection,
  DecisionSubHeading,
  DecisionCard,
  DecisionList,
} from "@/components/DecisionPageLayout";
import { InlineRelatedLinks } from "@/components/RelatedPages";
import { Clock, FileText, Brain } from "lucide-react";

const AlreadyTriedTms = () => (
  <DecisionPageLayout
    seo={{
      title:
        "I've Already Tried TMS — Can I Still Get SAINT? | Neuro Wellness FL",
      description:
        "Prior standard TMS does not disqualify you from SAINT. Learn about waiting periods, insurance considerations, and why partial response matters.",
      canonical: "/already-tried-tms",
      keywords:
        "tried TMS before, SAINT after TMS, TMS waiting period, partial TMS response, retry TMS",
      jsonLd: {
        "@context": "https://schema.org",
        "@type": "MedicalWebPage",
        name: "I've Already Tried TMS",
        about: "SAINT eligibility after prior standard TMS treatment",
      },
    }}
    badge="Prior Treatment"
    title="I've Already Tried TMS"
    subtitle="Having had standard TMS before does not disqualify you from SAINT. Here's what to know."
  >
    <DecisionSection heading="Prior TMS Is Not a Disqualifier">
      <p>
        Many patients who come to us for SAINT have already completed a course
        of standard TMS. Some had a partial response. Some had no response. Both
        are clinically relevant — and neither rules you out.
      </p>
    </DecisionSection>

    <DecisionCard>
      <div className="flex items-center gap-3">
        <Clock className="h-6 w-6 text-accent shrink-0" />
        <DecisionSubHeading>Waiting Periods Between Courses</DecisionSubHeading>
      </div>
      <p>
        Insurers typically require a waiting period between TMS treatment
        courses. The length of that waiting period varies by plan — some require
        several months, others longer.
      </p>
      <p>
        Bring the approximate end date of your last TMS treatment to your
        consultation. Our team will check your plan's specific requirements.
      </p>
    </DecisionCard>

    <DecisionSection>
      <div className="flex gap-3 items-start">
        <Brain className="h-5 w-5 text-accent shrink-0 mt-1" />
        <div className="space-y-3">
          <DecisionSubHeading>Why Partial Response Matters</DecisionSubHeading>
          <p>
            If you had a partial response to standard TMS — some improvement,
            but not enough — that is clinically useful information. It tells us
            your brain circuitry is responsive to magnetic stimulation. SAINT
            targets a different, individually-mapped pathway, which is why prior
            partial response doesn't disqualify you.
          </p>
          <p>
            If you had no response at all to standard TMS, that is also
            relevant. Your clinical team will factor it into the evaluation.
          </p>
        </div>
      </div>
    </DecisionSection>

    <DecisionSection>
      <DecisionSubHeading>
        What to Bring to Your Consultation
      </DecisionSubHeading>
      <DecisionList
        items={[
          "The approximate date your last TMS course ended (month and year is sufficient)",
          "Which TMS system or device was used, if you know",
          "Whether you experienced any improvement, and if so, how much and for how long",
          "Your current medication list",
          "Your insurance information",
        ]}
      />
    </DecisionSection>

    <DecisionSection>
      <DecisionSubHeading>The Bottom Line</DecisionSubHeading>
      <p>
        Prior TMS is part of your history, not a barrier. The consultation is
        where a clinical team reviews that history and determines whether SAINT
        is appropriate for you.
      </p>
    </DecisionSection>

    <InlineRelatedLinks
      heading="Explore Related Pages"
      links={[
        { title: "SAINT® Accelerated TMS", href: "/saint" },
        {
          title: "SAINT vs. Standard TMS",
          href: "/learn/saint-vs-traditional-tms",
        },
        { title: "Am I a Candidate?", href: "/am-i-a-candidate" },
        { title: "How Insurance Works", href: "/how-insurance-works" },
        { title: "Compare All Treatments", href: "/compare-treatments" },
      ]}
    />
  </DecisionPageLayout>
);

export default AlreadyTriedTms;
